import {
  ACESFilmicToneMapping,
  AmbientLight,
  Color,
  DirectionalLight,
  GridHelper,
  Group,
  Material,
  Mesh,
  PerspectiveCamera,
  Scene,
  SRGBColorSpace,
  WebGLRenderer,
  Vector3,
} from 'three';
import { CameraController } from '../camera/CameraController';
import { SelectionController } from '../selection/SelectionController';
import { SectionController } from '../section/SectionController';
import { BoxCutController, type BoxFace } from '../section/BoxCutController';
import type { ViewerTheme } from '../theme/ThemeController';

export class ViewerCore {
  readonly scene = new Scene();
  private readonly renderer: WebGLRenderer;
  private readonly camera = new PerspectiveCamera(50, 1, 0.01, 100_000);
  private readonly cameraController: CameraController;
  private readonly resizeObserver: ResizeObserver;
  readonly selection: SelectionController;
  readonly section: SectionController;
  readonly boxCut: BoxCutController;
  private grid: GridHelper;
  private frameId = 0;

  constructor(
    private readonly container: HTMLElement,
    readonly modelsRoot: Group = new Group(),
  ) {
    this.scene.background = new Color(0x0c111b);
    this.scene.add(this.modelsRoot);

    this.renderer = new WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.15;
    this.renderer.localClippingEnabled = true;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.domElement.className = 'viewer-canvas';
    this.container.append(this.renderer.domElement);

    this.camera.position.set(12, 10, 12);
    this.cameraController = new CameraController(this.camera, this.renderer.domElement);

    const ambient = new AmbientLight(0xffffff, 1.7);
    const keyLight = new DirectionalLight(0xffffff, 3.2);
    keyLight.position.set(20, 35, 15);
    const fillLight = new DirectionalLight(0x9bbcff, 1.4);
    fillLight.position.set(-20, 12, -10);
    this.grid = this.createGrid('dark');
    this.scene.add(ambient, keyLight, fillLight, this.grid);

    this.section = new SectionController(this.modelsRoot);
    this.boxCut = new BoxCutController(this.modelsRoot, this.scene);
    this.selection = new SelectionController(
      this.renderer.domElement,
      this.camera,
      this.modelsRoot,
      this.scene,
      (mesh) => this.fitObject(mesh),
      (point) => this.boxCut.containsPoint(point),
    );

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(container);
    this.resize();
    this.animate();
  }

  fitAll(): void {
    this.cameraController.fit(this.modelsRoot);
  }

  fitObject(object: Mesh): void {
    this.cameraController.fit(object);
  }

  setNavigationEnabled(enabled: boolean): void {
    this.cameraController.controls.enabled = enabled;
  }

  getSectionGizmoPosition(): { x: number; y: number; axisX: number; axisY: number; pixelsPerUnit: number } | null {
    const position = this.section.position;
    if (!position) return null;
    const direction = this.section.direction;
    const projected = position.clone().project(this.camera);
    if (projected.z < -1 || projected.z > 1) return null;
    const axisProjected = position.clone().add(direction).project(this.camera);
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    const x = (projected.x + 1) * width / 2;
    const y = (1 - projected.y) * height / 2;
    const axisVector = new Vector3(
      (axisProjected.x - projected.x) * width / 2,
      -(axisProjected.y - projected.y) * height / 2,
      0,
    );
    const pixelsPerUnit = Math.max(axisVector.length(), 0.5);
    axisVector.normalize();
    return { x, y, axisX: axisVector.x, axisY: axisVector.y, pixelsPerUnit };
  }

  getBoxGizmoPositions(): Array<{ face: BoxFace; x: number; y: number; axisX: number; axisY: number; pixelsPerUnit: number }> {
    return this.boxCut.handles.flatMap(({ face, point, direction }) => {
      const a = point.clone().project(this.camera);
      const b = point.clone().add(direction).project(this.camera);
      if (a.z < -1 || a.z > 1) return [];
      const width = this.container.clientWidth;
      const height = this.container.clientHeight;
      const axis = new Vector3((b.x - a.x) * width / 2, -(b.y - a.y) * height / 2, 0);
      const pixelsPerUnit = Math.max(axis.length(), 0.5);
      axis.normalize();
      return [{ face, x: (a.x + 1) * width / 2, y: (1 - a.y) * height / 2, axisX: axis.x, axisY: axis.y, pixelsPerUnit }];
    });
  }

  setTheme(theme: ViewerTheme): void {
    this.scene.background = new Color(theme === 'dark' ? 0x0c111b : 0xf2f5f9);
    this.scene.remove(this.grid);
    this.grid.geometry.dispose();
    const materials = Array.isArray(this.grid.material) ? this.grid.material : [this.grid.material];
    for (const material of materials) (material as Material).dispose();
    this.grid = this.createGrid(theme);
    this.scene.add(this.grid);
  }

  private createGrid(theme: ViewerTheme): GridHelper {
    const grid = theme === 'dark'
      ? new GridHelper(200, 40, 0x344054, 0x202b3c)
      : new GridHelper(200, 40, 0x8d99aa, 0xc8d0dc);
    grid.name = 'reference-grid';
    return grid;
  }

  private resize(): void {
    const width = Math.max(this.container.clientWidth, 1);
    const height = Math.max(this.container.clientHeight, 1);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  private animate = (): void => {
    this.frameId = requestAnimationFrame(this.animate);
    this.cameraController.update();
    this.renderer.render(this.scene, this.camera);
  };

  dispose(): void {
    cancelAnimationFrame(this.frameId);
    this.resizeObserver.disconnect();
    this.selection.dispose();
    this.cameraController.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
}

